import React from "react";
import BusinessIcon from "@mui/icons-material/Business";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  BoolBadge,
  DetailRow,
  DetailsBlock,
  formatBytes,
  formatDate,
  MetricTile,
  PanelCard,
  SectionHeader,
} from "./accountUi";

export type ClinicData = {
  clinicName?: string;
  clinicId?: string;
  address?: string;
  registerNo?: string;
  ownerName?: string;
  tpNo?: string;
  title?: string;
  regNoMethod?: string;
  regNoFormat?: string;
  reqNoStart?: string;
  startTime?: string;
  endTime?: string;
  timeDuration?: string;
  employeeCount?: number;
  advancedChart?: boolean;
  appointmentApp?: boolean;
  featureList?: {
    labs?: boolean;
    roomAllocation?: boolean;
    printTemplate_2?: boolean;
    doctorTransfer?: boolean;
  };
  appDocLimit?: number;
  logo?: boolean;
  logoimage?: string;
  printImages?: string;
  headerImages?: string;
  footerImages?: string;
  queueVideos?: {
    originalName?: string;
    mime?: string;
    size?: number;
    uploadedAt?: string;
  }[];
  createdAt?: string;
  updatedAt?: string;
};

function SubsectionTitle({ children }: { children: string }) {
  return (
    <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-8 mb-3 first:mt-0">
      <span className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent dark:from-slate-600" />
      <span className="shrink-0">{children}</span>
      <span className="h-px flex-1 bg-gradient-to-l from-slate-200 to-transparent dark:from-slate-600" />
    </h4>
  );
}

type ClinicTabPanelProps = {
  clinic: ClinicData | null;
  clinicNameFallback?: string;
  addressFallback?: string;
  phoneFallback?: string;
  logoUrl: string | null;
};

export default function ClinicTabPanel({
  clinic,
  clinicNameFallback,
  addressFallback,
  phoneFallback,
  logoUrl,
}: ClinicTabPanelProps) {
  const queueCount = clinic?.queueVideos?.length ?? 0;
  const displayName = clinic?.clinicName || clinicNameFallback || "—";

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-primary/[0.04] p-4 sm:p-5 dark:border-slate-700/80 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-primary/10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center xl:gap-8">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Clinic logo"
              className="h-24 w-24 shrink-0 rounded-2xl border border-slate-200/80 bg-white object-contain p-2 shadow-sm dark:border-slate-600 xl:h-28 xl:w-28"
            />
          ) : (
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primaryLight shadow-lg shadow-primary/20 xl:h-28 xl:w-28">
              <BusinessIcon sx={{ fontSize: 40 }} className="text-white" />
            </div>
          )}
          <div className="min-w-0 flex-1 xl:flex xl:items-end xl:justify-between xl:gap-6">
            <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              Clinic profile
            </p>
            <p className="mt-1 text-2xl font-bold capitalize tracking-tight text-slate-900 dark:text-white xl:text-3xl">
              {displayName}
            </p>
            {clinic?.clinicId ? (
              <p className="mt-1.5 inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                ID {clinic.clinicId}
              </p>
            ) : null}
            {(clinic?.address || addressFallback) && (
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 xl:max-w-xl">
                {clinic?.address || addressFallback}
              </p>
            )}
            </div>
            <div className="mt-4 hidden xl:grid xl:grid-cols-2 xl:gap-3 xl:mt-0">
              <MetricTile label="Staff limit" value={clinic?.employeeCount ?? "—"} />
              <MetricTile
                label="Appointment app"
                value={<BoolBadge value={clinic?.appointmentApp} />}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="xl:grid xl:grid-cols-12 xl:gap-4 xl:items-start">
        <PanelCard className="p-4 sm:p-5 xl:col-span-7">
          <SectionHeader icon={BusinessIcon} title="General information" />
          <div className="xl:grid xl:grid-cols-2 xl:gap-4">
            <div>
              <SubsectionTitle>General</SubsectionTitle>
              <DetailsBlock>
                <DetailRow label="Clinic name" value={displayName} />
                <DetailRow label="Clinic ID" value={clinic?.clinicId} />
                <DetailRow label="Bill / print title" value={clinic?.title} />
                <DetailRow label="Owner name" value={clinic?.ownerName} />
                <DetailRow label="Address" value={clinic?.address || addressFallback} />
                <DetailRow label="Telephone" value={clinic?.tpNo || phoneFallback} />
              </DetailsBlock>
            </div>
            <div>
              <SubsectionTitle>Registration &amp; hours</SubsectionTitle>
              <DetailsBlock>
                <DetailRow label="Registration no." value={clinic?.registerNo} />
                <DetailRow label="Logo on documents" value={<BoolBadge value={clinic?.logo} />} />
                <DetailRow label="Reg. no. method" value={clinic?.regNoMethod} />
                <DetailRow label="Reg. no. format" value={clinic?.regNoFormat} />
                <DetailRow label="Starting number" value={clinic?.reqNoStart} />
                <DetailRow label="Start time" value={clinic?.startTime} />
                <DetailRow label="End time" value={clinic?.endTime} />
                <DetailRow label="Slot duration" value={clinic?.timeDuration} />
              </DetailsBlock>
            </div>
          </div>
        </PanelCard>

        <div className="space-y-4 xl:col-span-5">
          <PanelCard className="p-4 sm:p-5">
            <SectionHeader
              icon={SettingsOutlinedIcon}
              title="Features & limits"
              subtitle="Enabled modules and capacity"
            />
            <div className="grid grid-cols-2 gap-3">
              <MetricTile
                label="Advanced chart"
                value={<BoolBadge value={clinic?.advancedChart} />}
              />
              <MetricTile
                label="Appointment app"
                value={<BoolBadge value={clinic?.appointmentApp} />}
              />
              <MetricTile
                label="Dental labs"
                value={<BoolBadge value={clinic?.featureList?.labs} />}
              />
              <MetricTile
                label="Room allocation"
                value={<BoolBadge value={clinic?.featureList?.roomAllocation} />}
              />
              <MetricTile
                label="Print template 2"
                value={<BoolBadge value={clinic?.featureList?.printTemplate_2} />}
              />
              <MetricTile
                label="Doctor transfer"
                value={<BoolBadge value={clinic?.featureList?.doctorTransfer} />}
              />
              <MetricTile label="App doctor limit" value={clinic?.appDocLimit ?? "—"} />
              <MetricTile label="Staff limit" value={clinic?.employeeCount ?? "—"} />
            </div>
            <div className="mt-4">
              <DetailsBlock>
                <DetailRow
                  label="Queue screen videos"
                  value={
                    queueCount > 0 ? `${queueCount} video${queueCount === 1 ? "" : "s"}` : "—"
                  }
                />
              </DetailsBlock>
              {queueCount > 0 ? (
                <ul className="mt-3 space-y-2">
                  {clinic!.queueVideos!.map((v, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm dark:border-slate-700/60 dark:bg-slate-900/30"
                    >
                      <span className="font-medium text-slate-800 dark:text-slate-200">
                        {v.originalName || `Video ${i + 1}`}
                      </span>
                      {v.size != null ? (
                        <span className="text-xs text-slate-500">{formatBytes(v.size)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </PanelCard>

          <PanelCard className="p-4 sm:p-5">
            <SectionHeader icon={ScheduleOutlinedIcon} title="Record" />
            <DetailsBlock>
              <DetailRow label="Created" value={formatDate(clinic?.createdAt)} />
              <DetailRow label="Last updated" value={formatDate(clinic?.updatedAt)} />
            </DetailsBlock>
          </PanelCard>
        </div>
      </div>

      {(clinic?.printImages || clinic?.headerImages || clinic?.footerImages) && (
        <PanelCard className="p-4 sm:p-5">
          <SectionHeader icon={PaletteOutlinedIcon} title="Branding images" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3">
            {[
              { label: "Print image", src: clinic?.printImages },
              { label: "Header image", src: clinic?.headerImages },
              { label: "Footer image", src: clinic?.footerImages },
            ].map(
              ({ label, src }) =>
                src && (
                  <div
                    key={label}
                    className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 dark:border-slate-600/80 dark:bg-slate-900/40"
                  >
                    <p className="border-b border-slate-100 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-700">
                      {label}
                    </p>
                    <img
                      src={src}
                      alt={label}
                      className="max-h-40 w-full object-contain p-3"
                    />
                  </div>
                )
            )}
          </div>
        </PanelCard>
      )}
    </div>
  );
}
